import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Activity, Cloud, Mail, RefreshCw, CheckCircle2, XCircle, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Super Admin Technical Health widget.
 *
 * One-stop panel showing:
 *   • Live API status pills for SolarOS and OpenWeather (latency + last
 *     error message when the last call failed).
 *   • Recent invite/report email log — success/failed labels + per-row
 *     "Retry" button that re-issues a token and re-sends via Resend.
 *   • Recent system_events (API failures, retries, cleanups) so admins
 *     have an at-a-glance operational audit trail.
 *
 * Polls every 30s. Failed retries surface the exact status via the
 * `sonner` toast so operators know whether Resend rejected the send.
 */
export default function SystemHealthWidget() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [retryingId, setRetryingId] = useState(null);

    const load = () => api.get("/admin/system/health")
        .then(r => setData(r.data))
        .catch(() => setData(null))
        .finally(() => setLoading(false));

    useEffect(() => {
        load();
        // Pause polling when the tab isn't visible — SyncBadge and this
        // widget together would otherwise send ≥4 req/min per admin.
        let t = null;
        const start = () => { if (!t) t = setInterval(load, 30000); };
        const stop = () => { if (t) { clearInterval(t); t = null; } };
        const onVis = () => document.visibilityState === "visible" ? start() : stop();
        start();
        document.addEventListener("visibilitychange", onVis);
        return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
    }, []);

    async function retry(id) {
        setRetryingId(id);
        try {
            const { data: res } = await api.post(`/admin/system/emails/${id}/retry`);
            if (res.success) toast.success("Retry succeeded — email delivered");
            else toast.error(`Retry status: ${res.status}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retry failed");
        } finally { setRetryingId(null); }
    }

    if (loading) {
        return (
            <div className="bg-white border border-[#E5E7EB] rounded-sm p-6 text-sm text-zinc-500 flex items-center gap-2" data-testid="system-health-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading technical health…
            </div>
        );
    }
    if (!data) return null;

    return (
        <div className="bg-white border border-[#E5E7EB] rounded-sm" data-testid="system-health-widget">
            <div className="px-6 py-4 border-b border-[#F3F4F6] flex items-center justify-between">
                <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Technical Health · Super Admin</div>
                    <div className="text-sm text-zinc-700 mt-0.5">Live API status &amp; email delivery log</div>
                </div>
                <button onClick={load} className="text-xs text-zinc-500 hover:text-[#0A0A0A] inline-flex items-center gap-1" data-testid="health-refresh">
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            {/* Operational-window banner — visible during 19:01–04:59 IST
                when the scheduler is paused and every response is served
                from the local Mongo snapshot. */}
            {data.operational_window && !data.operational_window.active && (
                <div className="mx-6 mt-4 mb-2 rounded-sm border border-indigo-300 bg-indigo-50 px-4 py-2.5 flex items-start gap-2.5" data-testid="night-mode-banner">
                    <span className="text-indigo-600 text-sm font-mono mt-0.5">☾</span>
                    <div className="text-xs text-indigo-800 leading-relaxed">
                        <span className="font-semibold">Night Mode Active.</span>{" "}
                        SolarOS polling is paused between{" "}
                        <span className="font-mono">{data.operational_window.end_hr_ist}:01</span> and{" "}
                        <span className="font-mono">{String(data.operational_window.start_hr_ist).padStart(2, "0")}:00</span> IST to preserve the daily API quota.
                        The dashboard is serving cached readings from the last daytime sync
                        {data.operational_window.last_synced_at && (
                            <> (<span className="font-mono">{new Date(data.operational_window.last_synced_at).toLocaleString("en-IN", { hour12: false })}</span>)</>
                        )}.
                    </div>
                </div>
            )}

            {/* 429 warning banner — surfaces whenever an upstream is rate-limited */}
            {(data.openweather?.rate_limited || data.solaros?.rate_limited || data.mail?.rate_limited) && (
                <div className="mx-6 mt-4 mb-2 rounded-sm border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-start gap-2.5" data-testid="rate-limit-banner">
                    <span className="text-amber-600 text-sm font-mono mt-0.5">429</span>
                    <div className="text-xs text-amber-800 leading-relaxed">
                        <span className="font-semibold">Upstream quota exceeded.</span>{" "}
                        {data.openweather?.rate_limited && <>OpenWeather returned <code className="font-mono">429 Too Many Requests</code> — raise the One Call 4.0 daily quota on the OpenWeather billing page or wait for the next reset window. </>}
                        {data.solaros?.rate_limited && <>SolarOS is currently rate-limited. </>}
                        {data.mail?.rate_limited && <>Resend is rate-limited. </>}
                        Reports and dashboards will fall back to cached observations until the quota resets.
                    </div>
                </div>
            )}

            {/* API pills — one live-status pill per upstream */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#F3F4F6]">
                <ApiPill label="SolarOS" icon={Activity} status={data.solaros} testid="api-solaros" />
                <ApiPill label="OpenWeather" icon={Cloud} status={data.openweather} testid="api-openweather" />
                <ApiPill label="Mail Server" icon={Mail} status={data.mail} testid="api-mail" />
            </div>

            {/* Full API Monitoring tabulation — Total / Failed / Success % */}
            {data.api_stats && data.api_stats.length > 0 && (
                <div className="border-t border-[#F3F4F6]">
                    <div className="px-6 py-3">
                        <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500">API Monitoring</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="api-stats-table">
                            <thead>
                                <tr className="border-t border-[#F3F4F6] text-[10px] uppercase tracking-widest text-zinc-500">
                                    <th className="text-left px-6 py-2 font-semibold">Connection</th>
                                    <th className="text-right px-4 py-2 font-semibold">Total Calls</th>
                                    <th className="text-right px-4 py-2 font-semibold">Failed</th>
                                    <th className="text-right px-4 py-2 font-semibold" title="Times the upstream returned HTTP 429">429s</th>
                                    <th className="text-right px-4 py-2 font-semibold">Success %</th>
                                    <th className="text-right px-6 py-2 font-semibold">Last Latency</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.api_stats.map((s, i) => (
                                    <tr key={s.source} className="border-t border-[#F3F4F6]" data-testid={`api-stat-${s.source}`}>
                                        <td className="px-6 py-2">
                                            <div className="flex items-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${s.reachable ? "bg-emerald-500" : "bg-red-500"}`} />
                                                <span className="font-medium text-zinc-800 capitalize">{s.source}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-right font-mono text-zinc-800 tabular-nums">{(s.total_calls || 0).toLocaleString()}</td>
                                        <td className={`px-4 py-2 text-right font-mono tabular-nums ${(s.failed_calls || 0) > 0 ? "text-red-600" : "text-zinc-500"}`}>{(s.failed_calls || 0).toLocaleString()}</td>
                                        <td className={`px-4 py-2 text-right font-mono tabular-nums ${(s.rate_limited_calls || 0) > 0 ? "text-amber-600 font-semibold" : "text-zinc-400"}`} data-testid={`rate-limited-${s.source}`}>{(s.rate_limited_calls || 0).toLocaleString()}</td>
                                        <td className={`px-4 py-2 text-right font-mono tabular-nums font-semibold ${s.success_rate >= 99 ? "text-emerald-600" : s.success_rate >= 90 ? "text-amber-600" : "text-red-600"}`}>{(s.success_rate ?? 100).toFixed(1)}%</td>
                                        <td className="px-6 py-2 text-right font-mono text-zinc-500 text-[11px]">{s.latency_ms != null ? `${s.latency_ms} ms` : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Email log */}
            <div className="border-t border-[#F3F4F6]">
                <div className="px-6 py-3 flex items-center justify-between">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> Recent Emails
                        <span className="ml-2 text-emerald-600">✓ {data.email.counts.sent}</span>
                        <span className="text-red-600">✕ {data.email.counts.failed}</span>
                    </div>
                </div>
                {data.email.log.length === 0 ? (
                    <div className="px-6 pb-6 text-sm text-zinc-500">No emails logged yet.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-t border-[#F3F4F6] text-[10px] uppercase tracking-widest text-zinc-500">
                                    <th className="text-left px-6 py-2 font-semibold">To</th>
                                    <th className="text-left px-4 py-2 font-semibold">Subject</th>
                                    <th className="text-left px-4 py-2 font-semibold">Status</th>
                                    <th className="text-left px-4 py-2 font-semibold">Sent</th>
                                    <th className="text-right px-6 py-2 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.email.log.map((e, i) => (
                                    <tr key={e.id || i} className="border-t border-[#F3F4F6]" data-testid={`email-row-${i}`}>
                                        <td className="px-6 py-2 text-zinc-800 font-mono text-[11px]">{e.to_email}</td>
                                        <td className="px-4 py-2 text-zinc-600 truncate max-w-[280px]">{e.subject}</td>
                                        <td className="px-4 py-2"><StatusChip status={e.status} /></td>
                                        <td className="px-4 py-2 text-[11px] text-zinc-500 font-mono">{formatDate(e.sent_at)}</td>
                                        <td className="px-6 py-2 text-right">
                                            {e.status !== "sent" && e.id && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => retry(e.id)}
                                                    disabled={retryingId === e.id}
                                                    className="h-7 rounded-sm border-zinc-200 text-[11px] hover:border-[#0A0A0A] hover:text-[#0A0A0A]"
                                                    data-testid={`email-retry-${i}`}
                                                >
                                                    {retryingId === e.id ? (
                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                    ) : (
                                                        <><RefreshCw className="w-3 h-3 mr-1" /> Retry</>
                                                    )}
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Recent events */}
            {data.recent_events && data.recent_events.length > 0 && (
                <div className="border-t border-[#F3F4F6] px-6 py-4">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-zinc-500 mb-2 inline-flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" /> Recent System Events
                    </div>
                    <ul className="space-y-1">
                        {data.recent_events.map((ev, i) => (
                            <li key={ev.id || i} className="text-[11px] text-zinc-600 font-mono flex items-baseline gap-2" data-testid={`event-row-${i}`}>
                                <span className="text-zinc-400 shrink-0">{formatDate(ev.ts)}</span>
                                <span className="uppercase tracking-widest text-[9px] text-zinc-500">{ev.kind}</span>
                                <span className="text-zinc-500">·</span>
                                <span className="text-zinc-700">{ev.source}</span>
                                <span className="text-zinc-500">·</span>
                                <span className="truncate">{ev.message}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function ApiPill({ label, icon: Icon, status, testid }) {
    const ok = !!status?.ok;
    const tone = ok ? "border-emerald-200 bg-emerald-50/60" : "border-red-200 bg-red-50/60";
    const valueColor = ok ? "text-emerald-700" : "text-red-700";
    return (
        <div className={`bg-white px-6 py-4 border-l-4 ${tone}`} data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <Icon className={`w-4 h-4 ${valueColor}`} />
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
                        <div className={`text-sm font-semibold ${valueColor}`}>{ok ? "Healthy" : "Down"}</div>
                    </div>
                </div>
                <div className="text-right text-[11px] font-mono">
                    {status?.latency_ms != null && <div className="text-zinc-500">{status.latency_ms}ms</div>}
                    <div className="text-zinc-600 truncate max-w-[220px]">{status?.message}</div>
                </div>
            </div>
        </div>
    );
}

function StatusChip({ status }) {
    if (status === "sent") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm"><CheckCircle2 className="w-3 h-3" /> Success</span>;
    if (status === "skipped_no_config") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded-sm">Skipped</span>;
    return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-700 bg-red-50 px-1.5 py-0.5 rounded-sm"><XCircle className="w-3 h-3" /> Failed</span>;
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}
